import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CalendarDays, Plus, Clock, User, Smile } from 'lucide-react';
import DictationInput from '../components/DictationInput';
import { formatDate } from '../lib/utils';

export default function AppointmentsPage() {
  const { user, axiosAuth } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(null);
  const [completeForm, setCompleteForm] = useState({ outcome: '', post_mood: 5 });
  const [form, setForm] = useState({ date: '', time: '', type: 'individual', with_user_id: '', notes: '' });
  const api = axiosAuth();
  const otherUsers = useMemo(() => users.filter(u => u.id !== user.id), [users, user.id]);

  const load = () => {
    api.get('/appointments').then(r => setAppointments(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const getUserName = (id) => users.find(u => u.id === id)?.full_name || '';

  const handleCreate = async () => {
    if (!form.date || !form.time) return toast.error('Date and time required');
    try {
      await api.post('/appointments', form);
      toast.success('Appointment scheduled!');
      setOpen(false);
      setForm({ date: '', time: '', type: 'individual', with_user_id: '', notes: '' });
      load();
    } catch { toast.error('Failed to create appointment'); }
  };

  const handleComplete = async (id) => {
    try {
      await api.put(`/appointments/${id}`, { status: 'completed', outcome: completeForm.outcome, post_mood: completeForm.post_mood });
      toast.success('Appointment completed! Mood recorded.');
      setCompleteOpen(null);
      setCompleteForm({ outcome: '', post_mood: 5 });
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleCancel = async (id) => {
    try {
      await api.put(`/appointments/${id}`, { status: 'cancelled' });
      toast.success('Appointment cancelled');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const statusColor = { scheduled: 'bg-sky-50 text-sky-700', completed: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-red-50 text-red-700' };
  const moodLabel = (v) => v <= 3 ? 'Struggling' : v <= 6 ? 'Okay' : 'Good';
  const moodColor = (v) => v <= 3 ? 'text-red-600' : v <= 6 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Appointments</h1><p className="text-muted-foreground text-sm mt-1">Manage your scheduled sessions</p></div>
        {user.role !== 'court_officer' && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-appointment-button"><Plus size={16} /> New Appointment</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Schedule Appointment</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-sm">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="appt-date-input" /></div>
                  <div className="space-y-1.5"><Label className="text-sm">Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="appt-time-input" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Meeting With</Label>
                  <Select value={form.with_user_id} onValueChange={v => setForm({...form, with_user_id: v})}>
                    <SelectTrigger data-testid="appt-with-user"><SelectValue placeholder="Select person" /></SelectTrigger>
                    <SelectContent>{otherUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual Session</SelectItem>
                      <SelectItem value="group">Group Session</SelectItem>
                      <SelectItem value="assessment">Assessment</SelectItem>
                      <SelectItem value="court">Court Related</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Notes (optional)</Label>
                  <DictationInput value={form.notes} onChange={(cleaned) => setForm(f => ({...f, notes: cleaned}))} placeholder="Add notes..." rows={2} data-testid="appointment-notes" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-appointment-button">Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {appointments.length > 0 ? appointments.map(a => (
          <Card key={a.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium capitalize">{a.type} Session</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Clock size={14} /> {a.date} at {a.time}</span>
                    {a.with_user_id && <span className="flex items-center gap-1"><User size={14} /> {getUserName(a.with_user_id)}</span>}
                  </div>
                  {a.notes && <p className="text-xs text-muted-foreground mt-1">{a.notes}</p>}
                  {a.outcome && <p className="text-xs mt-1 bg-emerald-50 rounded-lg px-2 py-1 inline-block text-emerald-700">Outcome: {a.outcome}</p>}
                  {a.post_mood && <span className={`text-xs ml-2 ${moodColor(a.post_mood)}`}><Smile size={12} className="inline mr-0.5" />Post-mood: {a.post_mood}/10 ({moodLabel(a.post_mood)})</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={statusColor[a.status] || 'bg-muted'} variant="secondary">{a.status}</Badge>
                  {a.status === 'scheduled' && user.role !== 'court_officer' && (
                    <div className="flex gap-1">
                      <Dialog open={completeOpen === a.id} onOpenChange={v => { setCompleteOpen(v ? a.id : null); if (!v) setCompleteForm({ outcome: '', post_mood: 5 }); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-emerald-600 text-xs h-7" data-testid={`complete-appt-${a.id}`}>Complete</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl">
                          <DialogHeader><DialogTitle>Complete Appointment</DialogTitle></DialogHeader>
                          <div className="space-y-4 pt-2">
                            <div className="space-y-1.5">
                              <Label className="text-sm">Outcome / Notes</Label>
                              <DictationInput value={completeForm.outcome} onChange={(cleaned) => setCompleteForm(f => ({...f, outcome: cleaned}))} placeholder="What was discussed? What was the outcome?" rows={3} data-testid="appointment-outcome" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm flex items-center gap-1"><Smile size={14} /> How are you feeling after this session?</Label>
                              <div className="flex items-center gap-3">
                                <input type="range" min={1} max={10} value={completeForm.post_mood} onChange={e => setCompleteForm({...completeForm, post_mood: parseInt(e.target.value)})} className="flex-1" data-testid="post-mood-slider" />
                                <span className={`text-sm font-bold w-16 text-right ${moodColor(completeForm.post_mood)}`}>{completeForm.post_mood}/10</span>
                              </div>
                              <div className="flex justify-between text-xs text-muted-foreground"><span>Struggling (1-3)</span><span>Okay (4-6)</span><span>Good (7-10)</span></div>
                            </div>
                            <Button onClick={() => handleComplete(a.id)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid="confirm-complete-appt">Complete & Save Mood</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="ghost" className="text-red-500 text-xs h-7" onClick={() => handleCancel(a.id)}>Cancel</Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center">
            <CalendarDays className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No appointments yet.</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
